import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { sameSession } from "../auth/session";
import { useSessionKey } from "../auth/useSessionKey";
import { logSlotChanges, slotChanges } from "../log/conferences";
import { maxAnnounce, placeSlot, programme, type Slot } from "../funnel/conferences";
import { ConferencePicker } from "../funnel/ConferencePicker";
import { useConferences } from "../funnel/useConferences";
import { useFunnelNav } from "../funnel/useFunnelNav";
import { useParticipation } from "../funnel/useParticipation";
import { fallbackOf } from "../settings/catalog";
import { useLocale, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * L'ÉTAPE CONFÉRENCES — le programme, et un créneau par conférence.
 *
 * ⚠️ ELLE ÉCRIT AU CLIC, comme la société et à la différence des dates. Une place se
 * prend : la garder dans l'état de navigation jusqu'à la confirmation la promettrait à
 * deux personnes à la fois, et la seconde l'apprendrait au bout du tunnel. Le
 * corollaire est assumé — quelqu'un qui abandonne son inscription garde ses créneaux
 * réservés, exactement comme dans la V1.
 *
 * ⚠️ RIEN N'EST OBLIGATOIRE ICI. La V1 ne désactive pas son bouton, et c'est juste :
 * une conférence est une opportunité, pas une case du dossier. « Continuer » passe donc
 * toujours, y compris sans aucun créneau.
 *
 * ⚠️ AUCUNE RÈGLE N'EST REJOUÉE DANS CET ÉCRAN. Complet, chevauchement, maximum,
 * collègue déjà inscrit : le serveur les a calculés et les rend créneau par créneau —
 * voir `funnel/conferences.ts`. Ici on les affiche, et on dit pourquoi.
 *
 * ⚠️ LA DISPOSITION EST CELLE DU PROTOTYPE, classes comprises : `.clist > .cvday >
 * .conf`, une LIGNE PAR SÉANCE groupée par jour et rangée par heure. Le premier jet
 * inventait une carte par conférence avec ses créneaux en tuiles — un autre objet, et
 * un vocabulaire de classes que le CSS client ne trouve pas. Voir `programme`.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback — EXCEPT the messages addressed to the ORGANISER (an empty catalog,
 * a step removed from the thread), French like the rest of the panel.
 */

const ICI = "/registration/conferences";

export function Conferences() {
  const t = useT();
  const event = useEvent();
  const client = useQueryClient();
  const { me } = useParticipation();
  const sessionKey = useSessionKey();

  const title = useText(
    "FO_MEETING_V2_CONFERENCES_TITLE",
    fallbackOf("FO_MEETING_V2_CONFERENCES_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_CONFERENCES_SUBTITLE",
    fallbackOf("FO_MEETING_V2_CONFERENCES_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_CONFERENCES_HTML");

  /* Toujours demandées ici : c'est l'écran de l'étape, et un organisateur qui l'ouvre
     après l'avoir retirée du parcours doit voir le programme, pas un blanc. */
  const { conferences, max, loading, failure } = useConferences(true);
  const limit = maxAnnounce(max, t);

  const locale = useLocale();
  const { previous, next, go } = useFunnelNav(ICI);

  const [sending, setSending] = useState(false);

  const ready = !!event && typeof me?.id === "number";

  /* La question et le refus sont au `ConferencePicker` ; ici on écrit, et un refus
     REMONTE jusqu'à lui pour qu'il en dise la cause. */
  const place = async (slot: Slot) => {
    if (!ready || sending) return;
    // ⚠️ Session changée depuis le rendu (autre onglet) : ce qui est ici est à l'ancien compte, rien ne part.
    if (!sameSession(sessionKey)) return;
    setSending(true);
    const changes = slotChanges(conferences, slot.id);
    try {
      await placeSlot(event!.domainId, event!.eventId, me!.id, slot.id);
      logSlotChanges(event, changes);
    } finally {
      /* ⚠️ TOUTE LA LISTE, PAS SEULEMENT CETTE CONFÉRENCE. Prendre un créneau ferme
         ceux qui le chevauchent — y compris dans les AUTRES conférences — et peut
         faire basculer le maximum atteint. Ne rafraîchir qu'une carte laisserait les
         voisines annoncer « Disponible » sur ce que le serveur refuse désormais.

         ⚠️ ET ON RAFRAÎCHIT AUSSI QUAND ÇA ÉCHOUE — surtout quand ça échoue : un refus
         veut dire que la liste affichée est fausse. Le chemin d'erreur n'invalidait
         rien, donc la ligne restait annoncée libre : on pouvait cliquer indéfiniment
         et relire le même reproche. */
      await client.invalidateQueries({ queryKey: ["conferences"] });
      setSending(false);
    }
  };

  /* Une seule mise en forme, calculée une fois : à plat, par jour, par heure — voir
     `programme`, qui porte la disposition du prototype. */
  const days = programme(conferences, locale, t);
  const empty = !loading && !failure && days.length === 0;

  return (
    <section className="page">
      <div className="ttl" data-cfg="conferencesTitle">
        <InPlace settingKey="FO_MEETING_V2_CONFERENCES_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_CONFERENCES_SUBTITLE" text={subtitle} as="p" />
      </div>

      {/* La limite AVANT de l'atteindre : sans elle, la troisième conférence était
          refusée sans que rien ne l'ait annoncé. Voir `maxAnnounce`. */}
      {limit && (
        <p className="hint" data-cfg="conferencesTitle">
          {limit}
        </p>
      )}

      {html.trim() !== "" && (
        <Prose
          html={html}
          className="se-prose"
          data-cfg="conferencesTitle"
          />
      )}

      {failure && (
        <p className="loginerr" role="alert">
          {t(
            /* ⚠️ PAS `PROGRAMME_NOT_LOADED` : le dictionnaire la porte déjà, avec une
               autre phrase (« Réessayez dans un instant »). La reprendre aurait
               remplacé ce que cet écran dit, sans que rien ne le signale. */
            "PROGRAMME_NOT_LOADED_RELOAD",
            "Le programme n'a pas pu être chargé. Rechargez la page, et prévenez l'organisateur si cela persiste.",
          )}
        </p>
      )}

      {loading && (
        <p className="empty">
          <span className="spin" aria-hidden="true" />
          <span className="a11y" role="status">
            {t("LOADING_PROGRAMME", 'Chargement du programme')}
          </span>
        </p>
      )}

      {/* ⚠️ LA LISTE VIDE A DEUX CAUSES, et l'organisateur doit pouvoir les
          distinguer : soit l'événement n'a aucune conférence, soit toutes celles
          qu'il a sont réservées à des catégories que la société de ce participant ne
          porte pas — la route filtre là-dessus. Le dire évite de chercher la panne
          dans le back office quand elle est dans le rattachement. */}
      <div className="alert" data-cfg="conferencesTitle" hidden={!empty}>
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>{t("NO_CONFERENCE_OFFERED", 'Aucune conférence à proposer')}</b>
          <small>
            Soit l'événement n'en déclare aucune au back office, soit toutes sont
            réservées à des catégories que la société de ce participant ne porte
            pas.
          </small>
        </span>
      </div>

      {/* Ancrée sur la famille de l'étape faute d'en avoir une à elle — le programme
          vient du back office, aucun réglage ne le gouverne. Même raison qu'à l'étape
          dates : sans `data-cfg`, le bloc sortirait de la trappe à CSS. */}
      <ConferencePicker
        days={days}
        who="self"
        cfg="conferencesTitle"
        busy={sending || !ready}
        place={place}
      />

      <div className="actions">
        {previous && (
          <Button type="button" variant="ghost" onClick={() => go(previous.path)}>
            {t("BACK", "Retour")}
          </Button>
        )}
        <Button
          type="button"
          loading={sending}
          disabled={!next}
          onClick={() => next && go(next.path)}
        >
          {t("CONTINUE", "Continuer")}
        </Button>
      </div>
    </section>
  );
}
